import { APP_VERSION } from 'app/utils/appVersion';
import { readSession, writeSession } from 'app/utils/safeSessionStorage';

// Keyed by build so a tab that heals onto a new build gets a fresh budget.
const reloadStateKey = () => `stale-chunk-reloads:${APP_VERSION}`;
const RETRY_WINDOW_MS = 60_000;
// A chunk can stay unreachable for reasons a reload cannot fix (blocked asset,
// partial upload), so cap the attempts.
const MAX_RELOADS = 2;
// Long enough that a committed reload has torn the page down first.
const RELOAD_REARM_MS = 10_000;

// Chrome/Firefox/Safari wordings, plus failed CSS chunk preloads.
const CHUNK_ERROR_PATTERN =
  /dynamically imported module|Importing a module script|Unable to preload CSS/i;

export const isChunkLoadError = (error: unknown): boolean =>
  error instanceof Error && CHUNK_ERROR_PATTERN.test(error.message);

let reloadRequested = false;

// Reloads already spent on this build. An unparseable value fails closed:
// refusing a reload only shows the error, bypassing the budget can loop.
const readReloadState = (): { at: number; count: number } => {
  const raw = readSession(reloadStateKey());
  if (!raw) return { at: 0, count: 0 };
  const spent = { at: 0, count: MAX_RELOADS };
  try {
    const parsed = JSON.parse(raw);
    const at = Number(parsed?.at);
    const count = Number(parsed?.count);
    if (!Number.isFinite(at) || !Number.isFinite(count)) return spent;
    return { at, count };
  } catch {
    return spent;
  }
};

// Reloads once onto the new build. Does nothing when a reload already ran and
// didn't help, so the error surfaces instead of the tab reloading forever.
export const requestStaleChunkReload = (): void => {
  if (reloadRequested) return;

  const { at, count } = readReloadState();
  if (Date.now() - at < RETRY_WINDOW_MS) return;
  if (count >= MAX_RELOADS) return;
  // No persisted marker means no loop guard; fail closed.
  const persisted = writeSession(
    reloadStateKey(),
    JSON.stringify({ at: Date.now(), count: count + 1 })
  );
  if (!persisted) return;

  reloadRequested = true;
  window.location.reload();
  // A beforeunload prompt can cancel the reload; re-arm so a later chunk
  // failure in this tab can still recover.
  window.setTimeout(() => {
    reloadRequested = false;
  }, RELOAD_REARM_MS);
};

// Vite fires this for every failed dynamic import — routes, wasm, vendor
// chunks — so one listener covers them all. Only a fetch failure means a stale
// tab; eval errors still propagate to their caller.
export const installStaleChunkReload = () => {
  window.addEventListener('vite:preloadError', (event) => {
    if (isChunkLoadError(event.payload)) {
      requestStaleChunkReload();
    }
  });
};
